/**
 * Abilities baked into the session token (BillerAPI `App\Services\Auth\Ability`).
 * The UI hides or disables what the credential cannot do; the API enforces it.
 */
export const ABILITIES = [
  'customers:read',
  'customers:write',
  'catalog:read',
  'catalog:write',
  'quotes:read',
  'quotes:write',
  'quotes:approve',
  'contracts:read',
  'contracts:write',
  'contracts:execute',
  'subscriptions:read',
  'subscriptions:write',
  'invoices:read',
  'invoices:write',
  'credit_notes:write',
  'payments:read',
  'payments:write',
  'refunds:write',
  'payment_methods:write',
  'receivables:read',
  'receivables:write',
  'entitlements:read',
  'entitlements:write',
  'usage:read',
  'usage:write',
  'reports:read',
  'audit:read',
  'webhooks:manage',
  'api_keys:manage',
  'gateways:manage',
  'settings:manage',
  'team:manage',
  'commissions:read',
  'commissions:manage',
  'commissions:approve',
] as const;

export type Ability = (typeof ABILITIES)[number];

/** Membership roles (BillerAPI `App\Services\Auth\Role`). */
export const ROLES = [
  { value: 'owner', label: 'Owner', description: 'Everything, including team and keys.' },
  {
    value: 'billing_admin',
    label: 'Billing admin',
    description: 'Catalog, commercial, quote approvals, billing, payments, settings.',
  },
  { value: 'collections', label: 'Collections', description: 'Receivables, payments and payment methods.' },
  { value: 'sales', label: 'Sales', description: 'Customers, quotes and contracts.' },
  { value: 'developer', label: 'Developer', description: 'API keys, webhooks and usage.' },
  { value: 'support', label: 'Support', description: 'Read access across billing.' },
  { value: 'read_only', label: 'Read only', description: 'Every read ability, no writes.' },
] as const;

export type Role = (typeof ROLES)[number]['value'];

export function roleLabel(role: string | null | undefined): string {
  if (!role) return 'API key';
  return ROLES.find((r) => r.value === role)?.label ?? role.replace(/_/g, ' ');
}

/** Group abilities by resource for display ("invoices" → ["read", "write"]). */
export function groupAbilities(abilities: readonly string[]): [string, string[]][] {
  const groups = new Map<string, string[]>();
  for (const ability of abilities) {
    const [resource = ability, action = ''] = ability.split(':');
    const list = groups.get(resource) ?? [];
    list.push(action);
    groups.set(resource, list);
  }
  return [...groups.entries()];
}
